/**
 * The Pro page as one form object (design_handoff_pro_setup/README.md "State management"), its
 * load from `/pro/me`, its save through `POST /pro` (+ `PUT /pro/pages/{id}/cover` for the framing
 * the page save cannot express), and the per-section dirty signatures the EDIT PAGE tab compares
 * against the last save. Write-through data — venue events, offers, working hours, specific dates,
 * calendar feeds — never lives here and never reads as unsaved.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { proService } from '../../../services';
import { newItem, newSection, defaultSections, sectionGates } from '../vendor/ListEditor';
import { isVendorKind } from '../vendor/vendorUi';
import { DEFAULT_RATIO } from '../../event/coverLayout';
import { visibleFor } from './proSetupSteps';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const emptyAvailability = () => DAYS.map((_, i) => ({ dayOfWeek: i, am: false, pm: false }));

export const blankForm = () => ({
  name: '', crafts: [], bio: '', baseCity: null, isCharity: false, isPublished: false, services: [],
  photos: [], team: [], links: [], availability: emptyAvailability(), flakeTiers: [],
  charityDocPath: null, charityDocUrl: null,
  logoPath: null, logoUrl: null, hideFaces: false, hideOwnerOnCover: false,
  cover: { layout: 'auto', split: 60, count: 0, cardId: null },
  kind: 'bookable', sections: defaultSections('bookable'), menuLayout: 'list', hours: [], happyHours: [], nights: [], attributes: [],
  where: { streetAddress: '', neighbourhood: '', parkingNote: '' },
  extras: { cuisine: '', priceBand: null, reserveUrl: '', orderUrl: '', websiteUrl: '', phone: '', brands: '', certifications: '', warrantyLine: '' },
  room: { capacity: null, doorsPolicy: '', ageMinimum: null, dressCode: '', coverRange: '', hireRateCents: null, hireIncludes: '', soundSpec: '', listForPartyStarters: false },
});

export const toSections = (dto) => (dto || []).map((s) => ({ ...newSection(s.name), id: s.id, note: s.note || '', layout: s.layout || null, photoPath: s.photoPath || null, photoUrl: s.photoUrl || null, showAbv: !!s.showAbv,
  items: (s.items || []).map((i) => ({ ...newItem(), id: i.id, name: i.name, description: i.description || '', priceCents: i.priceCents ?? null, priceText: i.priceText || '', marks: i.marks || [], abv: i.abv ?? null, style: i.style || '', durationMinutes: i.durationMinutes ?? null, bookMode: i.bookMode || 'none', photoPath: i.photoPath || null, photoUrl: i.photoUrl || null, isTopThing: !!i.isTopThing })) }));

/** Bookable pages keep ProService rows; the editor sees them as one section per section name. */
export const servicesToSections = (services) => {
  const groups = [];
  (services || []).forEach((s) => {
    const name = (s.sectionName || 'SERVICES').toUpperCase();
    let g = groups.find((x) => x.name === name); if (!g) { g = newSection(name); groups.push(g); }
    g.items.push({ ...newItem(), id: s.id, name: s.descriptionText, description: s.note || '', priceCents: s.priceCents ?? null, priceText: s.priceTerms || '', durationMinutes: s.durationMinutes ?? null, isBookable: !!s.isBookable, maxSessions: s.maxSessions ?? null, sessionDiscounts: s.sessionDiscounts || [], photoPath: s.photoPath || null, photoUrl: s.photoUrl || null, isTopThing: !!s.isTopThing });
  });
  return groups.length ? groups : defaultSections('bookable');
};
export const sectionsToServices = (sections) => sections.flatMap((s) => s.items.filter((i) => i.name.trim()).map((i) => ({
  descriptionText: i.name, priceTerms: i.priceText || '', durationMinutes: i.durationMinutes || null, priceCents: i.priceCents || null,
  isBookable: !!i.isBookable && i.durationMinutes > 0, maxSessions: i.maxSessions || null, sessionDiscounts: i.sessionDiscounts || [], sectionName: s.name || null, note: i.description || null, photoPath: i.photoPath || null, isTopThing: !!i.isTopThing,
})));

const num = (v, fallback = 50) => { const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : fallback; };

/** Everything the page save sends; `publish` overrides isPublished, `createNew` makes another page. */
export function buildPayload(form, { publish = null, createNew = false } = {}) {
  const vendor = isVendorKind(form.kind);
  return {
    name: form.name, crafts: form.crafts, bio: form.bio,
    baseCity: form.baseCity ? form.baseCity.label : '', latitude: form.baseCity ? form.baseCity.lat : null, longitude: form.baseCity ? form.baseCity.lng : null,
    isCharity: form.isCharity, isPublished: publish ?? form.isPublished,
    services: form.kind === 'bookable' ? sectionsToServices(form.sections) : [],
    photos: form.photos.filter((p) => !p.uploading).map((p) => ({ path: p.path || p.url, caption: p.caption || '' })).filter((p) => p.path),
    team: form.team.filter((t) => t.name.trim() && t.showOnPage !== false).map((t) => ({ name: t.name, role: t.role, avatarPath: t.avatarPath || null, linkedUserId: t.linkedUserId || null })),
    links: form.links.filter((l) => l.label.trim() && l.url.trim()),
    availability: form.availability.filter((a) => a.am || a.pm),
    timeZoneId: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; } })(),
    flakeTiers: form.flakeTiers.filter((t) => t.daysThreshold >= 0 && t.feePercent >= 0),
    charityDocPath: form.charityDocPath || null,
    logoPath: form.logoPath || null, hideFaces: !!form.hideFaces, hideOwnerOnCover: !!form.hideOwnerOnCover,
    kind: form.kind,
    menuLayout: form.menuLayout,
    menuSections: vendor && form.kind !== 'venue' ? form.sections.filter((s) => s.name.trim() || s.items.length).map((s) => ({ id: s.id || null, name: s.name || 'MENU', note: s.note || null, layout: s.layout, photoPath: s.photoPath, showAbv: !!s.showAbv, items: s.items.filter((i) => i.name.trim()).map((i) => ({ id: i.id || null, name: i.name, description: i.description || null, priceCents: i.priceCents, priceText: i.priceText || null, marks: i.marks || [], abv: i.abv, style: i.style || null, durationMinutes: i.durationMinutes, bookMode: i.bookMode || 'none', photoPath: i.photoPath, isTopThing: !!i.isTopThing })) })) : (vendor ? [] : null),
    hours: vendor ? form.hours : null,
    happyHours: form.kind === 'bar' ? form.happyHours : (vendor ? [] : null),
    recurringNights: form.kind === 'bar' ? form.nights : (vendor ? [] : null),
    attributes: vendor ? form.attributes : [],
    streetAddress: form.where.streetAddress, neighbourhood: form.where.neighbourhood, parkingNote: form.where.parkingNote,
    cuisine: form.extras.cuisine, priceBand: form.extras.priceBand, reserveUrl: form.extras.reserveUrl, orderUrl: form.extras.orderUrl, websiteUrl: form.extras.websiteUrl, phone: form.extras.phone,
    brands: form.extras.brands, certifications: form.extras.certifications, warrantyLine: form.extras.warrantyLine,
    capacity: form.room.capacity, doorsPolicy: form.room.doorsPolicy, ageMinimum: form.room.ageMinimum, dressCode: form.room.dressCode, coverRange: form.room.coverRange,
    hireRateCents: form.room.hireRateCents, hireIncludes: form.room.hireIncludes, soundSpec: form.room.soundSpec, listForPartyStarters: form.room.listForPartyStarters,
    // Events on the page are linked live — never through this payload.
    venueEventIds: null,
    ...(createNew ? { createNew: true } : {}),
  };
}

/** The slice of the form each section owns — what "unsaved" compares. */
export const SIG = {
  identity: (d) => [d.name, d.crafts, d.baseCity, d.bio, d.isCharity, d.charityDocPath],
  kind: (d) => d.kind,
  list: (d) => d.kind === 'venue' ? null : d.sections.map((s) => [s.name, s.note, s.showAbv, s.items.map((i) => [i.name, i.description, i.priceCents, i.priceText, i.durationMinutes, i.isBookable, i.maxSessions, i.sessionDiscounts, i.bookMode, i.marks, i.abv, i.style, i.photoPath])]),
  extras: (d) => [d.extras, d.room],
  schedule: (d) => (d.kind === 'bookable' ? null : [d.hours, d.happyHours, d.nights]),
  where: (d) => d.where,
  attributes: (d) => d.attributes,
  layout: (d) => [d.menuLayout, d.sections.map((s) => [s.layout, s.photoPath])],
  top: (d) => d.sections.map((s) => s.items.map((i) => i.isTopThing)),
  offers: () => null,
  photos: (d) => [d.photos.map((p) => [p.path, p.fx, p.fy]), d.cover, d.logoPath, d.hideOwnerOnCover, d.hideFaces, d.team.map((t) => [t.name, t.role, t.avatarPath, t.linkedUserId, t.showOnPage])],
  publish: (d) => [d.links, d.flakeTiers, d.isPublished],
};

export const dirtySections = (form, saved) => (saved ? visibleFor(form.kind).filter((k) => JSON.stringify(SIG[k](form)) !== JSON.stringify(SIG[k](saved))) : []);

/** Sentence-case blockers: what stops a save / publish, with the field each one points at. */
export function blockersFor(form) {
  const out = [];
  if (!form.name.trim()) out.push({ text: 'Give your business a name', field: 'name', section: 'identity' });
  if (!form.crafts.length) out.push({ text: 'Pick at least one lane and one role', field: 'roles', section: 'identity' });
  if (isVendorKind(form.kind) && form.kind !== 'venue') {
    form.sections.forEach((s) => {
      const g = sectionGates(s);
      if ((s.layout || form.menuLayout) === 'plate' && s.items.length > 0 && !g.plateOk) out.push({ text: `Add a photo to every item in ${s.name || 'that section'} for PLATE (${g.withPhoto} of ${s.items.length})`, field: 'plate', section: 'list' });
    });
  }
  return out;
}

/** A page from the API → the form (plus the creator's face at the top of the team). */
export function formFromPage(d, { profile, user } = {}) {
  const f = blankForm();
  const avail = emptyAvailability();
  (d.availability || []).forEach((a) => { if (avail[a.dayOfWeek]) { avail[a.dayOfWeek].am = a.am; avail[a.dayOfWeek].pm = a.pm; } });
  const kind = d.kind || 'bookable';
  return {
    ...f,
    name: d.name || '', crafts: d.crafts || [], bio: d.bio || '',
    logoPath: d.logoPath || null, logoUrl: d.logoUrl || null, hideFaces: !!d.hideFaces, hideOwnerOnCover: !!d.hideOwnerOnCover,
    baseCity: d.latitude != null && d.longitude != null ? { label: d.baseCity || '', lat: d.latitude, lng: d.longitude } : (d.baseCity ? { label: d.baseCity, lat: null, lng: null } : null),
    isCharity: !!d.isCharity, isPublished: !!d.isPublished,
    services: d.services || [],
    photos: (d.photos || []).map((p) => ({ id: p.id, path: p.path || '', url: p.url, caption: p.caption || '', fx: num(p.focalX), fy: num(p.focalY), r: DEFAULT_RATIO })),
    cover: { layout: d.coverLayout || 'auto', split: [60, 50, 40].includes(Number(d.coverSplit)) ? Number(d.coverSplit) : 60, count: Number(d.coverPhotoCount || 0), cardId: d.cardPhotoId || null },
    team: withCreatorFace((d.team || []).map((t) => ({ name: t.name, role: t.role || '', avatarPath: '', avatarUrl: t.avatarUrl || '', linkedUserId: t.linkedUserId || null, linkedHandle: t.linkedHandle || null, showOnPage: true })), { profile, user }),
    links: (d.links || []).map((l) => ({ label: l.label, url: l.url })),
    availability: avail,
    flakeTiers: (d.flakeTiers || []).map((x) => ({ comparator: x.comparator, daysThreshold: x.daysThreshold, feePercent: x.feePercent })),
    charityDocPath: null, charityDocUrl: d.charityDocUploaded ? 'uploaded' : null,
    kind,
    sections: kind === 'bookable' ? servicesToSections(d.services) : (d.menuSections?.length ? toSections(d.menuSections) : defaultSections(kind)),
    menuLayout: d.menuLayout || 'list',
    hours: d.hours || [], happyHours: (d.happyHours || []).map((h) => ({ ...h })), nights: (d.recurringNights || []).map((n) => ({ ...n })), attributes: d.attributes || [],
    where: { streetAddress: d.streetAddress || '', neighbourhood: d.neighbourhood || '', parkingNote: d.parkingNote || '' },
    extras: { cuisine: d.cuisine || '', priceBand: d.priceBand ?? null, reserveUrl: d.reserveUrl || '', orderUrl: d.orderUrl || '', websiteUrl: d.websiteUrl || '', phone: d.phone || '', brands: d.brands || '', certifications: d.certifications || '', warrantyLine: d.warrantyLine || '' },
    room: { capacity: d.capacity ?? null, doorsPolicy: d.doorsPolicy || '', ageMinimum: d.ageMinimum ?? null, dressCode: d.dressCode || '', coverRange: d.coverRange || '', hireRateCents: d.hireRateCents ?? null, hireIncludes: d.hireIncludes || '', soundSpec: d.soundSpec || '', listForPartyStarters: !!d.listForPartyStarters },
  };
}

/** The creator (the signed-in person) always leads the faces; they can hide themselves but not leave. */
export function withCreatorFace(team, { profile, user } = {}) {
  const meId = profile?.userId || user?.id || null;
  const meName = (profile?.displayName || '').trim().toLowerCase();
  const idx = team.findIndex((t) => (t.linkedUserId && meId && t.linkedUserId === meId) || (meName && (t.name || '').trim().toLowerCase() === meName));
  const creator = { name: profile?.displayName || '', role: 'owner', avatarPath: '', avatarUrl: profile?.avatarUrl || '', linkedUserId: meId || null, linkedHandle: profile?.handle || null, showOnPage: true, isCreator: true };
  if (idx >= 0) {
    const up = { ...team[idx], role: team[idx].role || 'owner', isCreator: true, avatarUrl: team[idx].avatarUrl || creator.avatarUrl, linkedUserId: team[idx].linkedUserId || creator.linkedUserId, linkedHandle: team[idx].linkedHandle || creator.linkedHandle, showOnPage: team[idx].showOnPage !== false };
    return [up, ...team.slice(0, idx), ...team.slice(idx + 1)];
  }
  return [creator, ...team];
}

/**
 * useProPageForm({ creatingNew, profile, user })
 *   → { form, setForm, up(fn), page, loading, error, setError, saving, save, reload, saved, dirty, blockers, craftGroups, crafts, provenance, setProvenance }
 *
 * `saved` is the form as of the last load/save; `dirty` the visible sections whose slice differs.
 */
export default function useProPageForm({ creatingNew = false, profile, user, preloaded = null } = {}) {
  const [form, setForm] = useState(blankForm);
  const [saved, setSaved] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [crafts, setCrafts] = useState([]);
  const [craftGroups, setCraftGroups] = useState([]);
  const [provenance, setProvenance] = useState({ url: null, at: null });
  const identity = useRef({ profile, user });
  identity.current = { profile, user };

  const applyLoaded = useCallback((d) => {
    setPage(d);
    const f = formFromPage(d, identity.current);
    setForm(f);
    setSaved(f);
    setProvenance({ url: d.menuImportUrl || null, at: d.menuImportedAt || null });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // `preloaded` = { page } when the caller already fetched /pro/me (the setup page's published-page gate).
      const [c, g, mine] = await Promise.all([proService.getCrafts(), proService.getCraftGroups(), creatingNew ? Promise.resolve({ success: false }) : preloaded ? Promise.resolve({ success: !!preloaded.page, data: preloaded.page }) : proService.getMine()]);
      if (cancelled) return;
      if (g.success && g.data?.groups) setCraftGroups(g.data.groups);
      if (c.success) setCrafts(c.data || []);
      if (mine.success && mine.data) applyLoaded(mine.data);
      else {
        const f = { ...blankForm(), team: withCreatorFace([], identity.current) };
        setForm(f);
        setSaved(null);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const up = useCallback((fn) => setForm((f) => { const n = fn(f); return n === undefined ? f : n; }), []);

  const reload = useCallback(async () => {
    const r = await proService.getMine();
    if (r?.success && r.data) {
      setPage(r.data);
      // A reload refreshes the saved snapshot and the write-through parts only; typing survives.
      const f = formFromPage(r.data, identity.current);
      setSaved(f);
      setProvenance({ url: r.data.menuImportUrl || null, at: r.data.menuImportedAt || null });
      return r.data;
    }
    return null;
  }, []);

  /**
   * Save the whole form. Returns the saved page or null. The cover framing (focal points, layout,
   * count, card) rides a second call — the page save replaces photo rows by path and carries the
   * old framing with them, so the new one is PUT afterwards against the ids that came back.
   */
  const save = useCallback(async ({ publish = null, createNew = false } = {}) => {
    setSaving(true); setError('');
    const r = await proService.save(buildPayload(form, { publish, createNew }));
    if (!r.success) { setSaving(false); setError(r.error || 'Could not save.'); return null; }
    let final = r.data;
    const hasPhotos = form.photos.some((p) => !p.uploading && (p.path || p.url));
    if (hasPhotos && final?.id) {
      const byPath = new Map((final.photos || []).map((p) => [p.path, p]));
      const ordered = form.photos.filter((p) => !p.uploading).map((p) => byPath.get(p.path || p.url)).filter(Boolean);
      const payload = {
        order: ordered.map((p) => p.id),
        photoCount: form.cover.count || null,
        layout: form.cover.layout || 'auto',
        split: form.cover.split || 60,
        focal: form.photos.filter((p) => !p.uploading).map((p) => { const row = byPath.get(p.path || p.url); return row ? { id: row.id, x: num(p.fx), y: num(p.fy) } : null; }).filter(Boolean),
      };
      const card = form.cover.cardId ? form.photos.find((p) => p.id === form.cover.cardId) : null;
      const cardRow = card ? byPath.get(card.path || card.url) : null;
      if (cardRow) payload.cardId = cardRow.id;
      const c = await proService.updateCover(final.id, payload);
      if (c?.success) { const again = await proService.getMine(); if (again?.success && again.data) final = again.data; }
    }
    applyLoaded(final);
    setSaving(false);
    return final;
  }, [form, applyLoaded]);

  const dirty = useMemo(() => dirtySections(form, saved), [form, saved]);
  const blockers = useMemo(() => blockersFor(form), [form]);

  return { form, setForm, up, page, loading, error, setError, saving, save, reload, saved, dirty, blockers, crafts, craftGroups, provenance, setProvenance, applyLoaded };
}
